import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import distributorService, {
  DISTRIBUTOR_ACTIVITY_PAGE_SIZE,
} from '../../services/distributorService';
import DistributorShipmentDialog from './DistributorShipmentDialog';
import DistributorActivityHeader, { DistributorActivityFilters } from './DistributorActivityHeader';
import StatusBadge from '../StatusBadge';
import useShippingRequest from '../../hooks/useShippingRequest';
import OrderDetailsModal from '../../features/orders/OrderDetailsModal';
import IVRDetailsModal from '../../features/ivr/IVRDetailsModal';
import OrderListTable from '../../features/orders/OrderListTable';
import IVRListTable from '../../features/ivr/IVRListTable';
import { ivrOutcome } from '../../utils/ivrOutcome';
import { IVR_STATUS_CONFIG } from '../../features/ivr/ivrPresentation';
import { useLocale } from '../../i18n/LanguageContext';
import reportService, { saveExportResponse } from '../../services/reportService';
import '../../styles/distributor-workspace.css';
import DistributorShipmentTable from './DistributorShipmentTable';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import ShipmentListTable from '../../features/shipments/ShipmentListTable';

const ACTIVITY = {
  orders: { title: 'Orders', list: 'listOrders', detail: 'getOrder' },
  ivrs: { title: 'IVRs', list: 'listIvrs', detail: 'getIvr' },
  shipments: { title: 'Shipments', list: 'listShipments', detail: 'getShipment' },
};

export function DistributorActivity({
  t,
  onlyShipments = false,
  activityKind,
  adminDistributorId,
  adminOverview = false,
  distributors = [],
  pageHeader,
  statusFilter,
  onStatusFilterChange,
  active = true,
  refresh = 0,
  children,
}) {
  const locale = useLocale();
  const fixedKind = onlyShipments
    ? 'shipments'
    : Object.keys(ACTIVITY).includes(activityKind)
      ? activityKind
      : null;
  const { begin, version } = useShippingRequest(
    `${adminDistributorId || ''}:${fixedKind || 'distributor-activity'}:${active}`
  );
  const [kind, setKind] = useState(fixedKind || 'orders');
  const selectedKind = fixedKind || kind;
  const pageSize = DISTRIBUTOR_ACTIVITY_PAGE_SIZE;
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const filterScope = `${version}:${adminDistributorId || ''}:${fixedKind || kind}:${active}`;
  const [filters, setFilters] = useState({});
  const [draft, setDraft] = useState({});
  const criteria = useMemo(() => {
    const scoped = filters.scope === filterScope ? filters : {};
    return statusFilter === undefined ? scoped : { ...scoped, status: statusFilter };
  }, [filters, filterScope, statusFilter]);
  const inputs = draft.scope === filterScope ? draft : {};
  const changeInput = (key, value) => setDraft({ ...inputs, scope: filterScope, [key]: value });
  const changeStatus = (value) => {
    if (statusFilter !== undefined) onStatusFilterChange?.(value);
    else setFilters({ ...criteria, scope: filterScope, status: value });
  };
  const filterPayload = useMemo(
    () =>
      Object.fromEntries(
        ['search', 'dateFrom', 'dateTo']
          .filter((key) => criteria[key])
          .map((key) => [key, criteria[key]])
      ),
    [criteria]
  );
  const changeFilters = useCallback(
    (next) => {
      setFilters((current) => {
        const previous = current.scope === filterScope ? current : {};
        if (Object.entries(next).every(([key, value]) => (previous[key] || '') === value))
          return current;
        return { ...previous, scope: filterScope, ...next };
      });
    },
    [filterScope]
  );
  const filtered = Object.keys(filterPayload).length > 0 || !!criteria.status;
  const [detail, setDetail] = useState(null);
  const [failedDetailId, setFailedDetailId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const exportRequest = useRef(null);
  const { begin: beginExport } = useShippingRequest(
    `${adminDistributorId || ''}:${fixedKind || kind}:${JSON.stringify(criteria)}:${active}:export`
  );
  const request = useRef(0);
  const detailAccess = useRef(null);
  const detailScope = useRef(null);
  useEffect(() => {
    exportRequest.current = null;
    setExporting(false);
    setExportError('');
  }, [version, kind, criteria, active]);
  useEffect(() => {
    if (!active) {
      setFilters({});
      setDraft({});
      setDetail(null);
    }
  }, [active]);
  useEffect(
    () => () => {
      request.current += 1;
      exportRequest.current = null;
    },
    []
  );
  const open = useCallback(
    async (nextKind, nextPage = 1) => {
      const generation = ++request.current;
      const scopedRequest = begin();
      const current = () => generation === request.current && scopedRequest.isCurrent();
      setKind(nextKind);
      setPage(nextPage);
      setRows(null);
      setDetail(null);
      setFailedDetailId(null);
      setError('');
      setLoading(true);
      try {
        const extra = Object.keys(filterPayload).length ? [filterPayload] : [];
        const response = await (adminOverview
          ? distributorService.adminActivityOverview(
              nextKind,
              adminDistributorId,
              nextPage,
              criteria.status || undefined,
              ...extra
            )
          : adminDistributorId
            ? distributorService.adminActivity(
                nextKind,
                adminDistributorId,
                nextPage,
                criteria.status || undefined,
                ...extra
              )
            : nextKind === 'orders'
              ? distributorService.listOrders(
                  nextPage,
                  ...(criteria.status || extra.length
                    ? [criteria.status || undefined, ...extra]
                    : [])
                )
              : distributorService[ACTIVITY[nextKind].list](
                  nextPage,
                  ...(criteria.status || extra.length
                    ? [
                        {
                          ...filterPayload,
                          ...(criteria.status
                            ? {
                                [nextKind === 'shipments' ? 'orderStatus' : 'status']:
                                  criteria.status,
                              }
                            : {}),
                        },
                      ]
                    : [])
                ));
        const result = response?.data?.data;
        if (
          !Array.isArray(result?.data) ||
          !Number.isSafeInteger(result?.total) ||
          result.total < 0
        )
          throw new Error('Invalid activity list');
        if (current()) {
          setRows(result.data);
          setTotal(result.total);
        }
      } catch {
        if (current()) setError('Could not load activity. Please try again.');
      } finally {
        if (current()) setLoading(false);
      }
    },
    [begin, criteria.status, filterPayload, adminDistributorId, adminOverview]
  );
  useEffect(() => {
    if (active) open(selectedKind);
  }, [selectedKind, open, version, active, refresh]);
  const view = async (id) => {
    const generation = ++request.current;
    const scopedRequest = begin();
    const current = () => generation === request.current && scopedRequest.isCurrent();
    setDetail(null);
    setFailedDetailId(null);
    setError('');
    setLoading(true);
    try {
      const record = rows?.find((row) => row._id === id);
      const recordScope =
        adminDistributorId ||
        record?.distributorId?._id ||
        record?.distributorId ||
        record?.order?.distributorId?._id ||
        record?.order?.distributorId;
      const response = await (adminDistributorId || adminOverview
        ? distributorService.adminActivityDetail(kind, recordScope, id)
        : distributorService[ACTIVITY[kind].detail](id));
      const row = response?.data?.data;
      if (!row?._id || row._id !== id) throw new Error('Invalid activity detail');
      if (current()) {
        detailAccess.current = version;
        detailScope.current = recordScope;
        setDetail(row);
      }
    } catch {
      if (current()) {
        setFailedDetailId(id);
        setError('Could not open this record. Please try again.');
      }
    } finally {
      if (current()) setLoading(false);
    }
  };
  // Follow ShipmentTracking: a completed shipment wins; otherwise use its parent order.
  const status = (row) => {
    if (kind === 'orders' && row.status === 'submitted') return 'Order received \u2014 processing';
    const value =
      kind === 'shipments'
        ? row.status === 'completed'
          ? 'completed'
          : (row.orderStatus ?? row.order?.status)
        : row.status;
    return [
      'approved',
      'shipped',
      'completed',
      'cancelled',
      'submitted',
      'covered',
      'not_covered',
      'needs_review',
      'rejected',
    ].includes(value)
      ? value
      : 'Status unavailable';
  };
  const formatActivityDate = (value) =>
    value && Number.isFinite(new Date(value).getTime())
      ? new Date(value).toLocaleDateString(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : t('Not set');
  const exportOrders = async () => {
    if (exportRequest.current) return;
    const reservation = {};
    const session = captureSession();
    const scopedRequest = beginExport();
    const current = () =>
      exportRequest.current === reservation &&
      scopedRequest.isCurrent() &&
      isSessionCurrent(session);
    exportRequest.current = reservation;
    setExporting(true);
    setExportError('');
    try {
      const response = await (adminDistributorId || adminOverview
        ? reportService.exportMyOrders({
            businessContext: 'distributor',
            ...(adminDistributorId ? { distributorId: adminDistributorId } : {}),
            ...filterPayload,
            ...(criteria.status ? { status: criteria.status } : {}),
          })
        : filtered
          ? reportService.exportMyOrders({
              ...filterPayload,
              ...(criteria.status ? { status: criteria.status } : {}),
            })
          : reportService.exportMyOrders());
      if (current()) saveExportResponse(response, 'distributor-orders');
    } catch {
      if (current()) setExportError('Could not export orders. Please try again.');
    } finally {
      if (current()) setExporting(false);
      if (exportRequest.current === reservation) exportRequest.current = null;
    }
  };
  return (
    <section
      aria-label={t(fixedKind ? ACTIVITY[fixedKind].title : 'Distributor activity')}
      className={
        adminOverview
          ? undefined
          : `rounded-xl border p-5 ${pageHeader ? 'me-dw-page-activity' : 'space-y-4'} sm:p-6 ${onlyShipments ? 'border-slate-200 bg-white' : `me-dw-card me-dw-activity${kind !== 'shipments' || pageHeader ? ' me-dw-client-activity' : ''}`}`
      }
    >
      {pageHeader && (
        <DistributorActivityHeader
          key={filterScope}
          kind={kind}
          t={t}
          headingRef={active ? pageHeader.headingRef : undefined}
          createAction={pageHeader.createAction}
          criteria={criteria}
          onFiltersChange={changeFilters}
          exporting={exporting}
          onExport={exportOrders}
          title={pageHeader.title}
          subtitle={pageHeader.subtitle}
          filterAction={pageHeader.filterAction}
          adminLayout={adminOverview}
        />
      )}
      {children}
      {!onlyShipments && !pageHeader && (
        <div className="me-dw-card-head flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold tracking-tight text-[#153d48]">
            {t(fixedKind ? ACTIVITY[fixedKind].title : 'Distributor activity')}
          </h2>
          {kind === 'orders' && (
            <button
              type="button"
              className="me-dw-link text-sm"
              disabled={exporting}
              onClick={exportOrders}
            >
              {t(exporting ? 'Exporting...' : 'Export orders')}
            </button>
          )}
        </div>
      )}
      {!fixedKind && (
        <div className="flex flex-wrap gap-2" aria-label={t('Distributor activity')}>
          {Object.entries(ACTIVITY).map(([key, value]) => (
            <button
              key={key}
              type="button"
              aria-pressed={kind === key}
              className="me-dw-activity-tab rounded-lg border px-4 py-2 text-sm font-semibold"
              onClick={() => setKind(key)}
            >
              {t(value.title)}
            </button>
          ))}
        </div>
      )}
      {!pageHeader && (
        <DistributorActivityFilters
          {...{ t, kind, inputs, changeInput, filtered }}
          onSubmit={(event) => {
            event.preventDefault();
            changeFilters({
              search: (inputs.search || '').trim(),
              dateFrom: inputs.dateFrom || '',
              dateTo: inputs.dateTo || '',
            });
          }}
          onClear={() => {
            setDraft({});
            setFilters({});
          }}
        />
      )}
      <div
        className={`flex flex-wrap items-center gap-2${pageHeader ? ' mb-6' : ''}`}
        role="group"
        aria-label={t('Status filter')}
      >
        {(kind === 'orders'
          ? [
              ['', 'All'],
              ['submitted', 'Processing'],
              ['approved', 'Approved'],
              ['shipped', 'Shipped'],
              ['completed', 'Completed'],
              ['cancelled', 'Cancelled'],
            ]
          : kind === 'ivrs'
            ? [
                ['', 'All'],
                ['submitted', 'Submitted'],
                ['needs_review', 'Needs Review'],
                ['covered', IVR_STATUS_CONFIG.covered.label],
                ['not_covered', IVR_STATUS_CONFIG.not_covered.label],
                ['rejected', 'Rejected'],
              ]
            : [
                ['', 'All'],
                ['approved', 'Approved'],
                ['shipped', 'Shipped'],
                ['completed', 'Completed'],
                ['cancelled', 'Cancelled'],
              ]
        ).map(([value, title]) => (
          <button
            type="button"
            key={value}
            className={`${adminOverview ? '' : 'me-dw-activity-tab '}px-4 h-10 rounded-lg text-[13px] font-semibold transition-colors inline-flex items-center gap-1.5 ${
              (criteria.status || '') === value
                ? adminOverview
                  ? 'bg-[#0f172a] text-white'
                  : 'bg-[#0b7378] text-white'
                : 'bg-[#f7f8fb] text-[#64748b] hover:bg-[#e2e8f0]'
            }`}
            aria-pressed={(criteria.status || '') === value}
            onClick={() => changeStatus(value)}
          >
            {t(title)}
          </button>
        ))}
      </div>
      {loading && <p role="status">{t('Loading activity...')}</p>}
      {exportError && <p role="alert">{t(exportError)}</p>}
      {error && (
        <p role="alert" className="text-red-700">
          {t(error)}{' '}
          {kind && (
            <button
              type="button"
              className="underline"
              onClick={() => (failedDetailId ? view(failedDetailId) : open(kind, page))}
            >
              {t('Retry')}
            </button>
          )}
        </p>
      )}
      {kind && rows?.length === 0 && (
        <p className="me-dw-subpanel p-5 text-sm text-slate-600">
          {filtered
            ? t('No records match these filters.')
            : t('No {kind} yet.', {
                kind: kind === 'ivrs' ? t('IVRs') : t(ACTIVITY[kind].title).toLowerCase(),
              })}
        </p>
      )}
      {kind && rows && (
        <div
          className={
            adminOverview
              ? 'bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-x-auto'
              : kind !== 'shipments' || pageHeader
                ? 'me-dw-client-list bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-hidden'
                : undefined
          }
        >
          {(rows.length > 0 || adminOverview) && (
            <div
              className={
                adminOverview
                  ? 'overflow-x-auto'
                  : `me-dw-activity-table-scroll${kind !== 'shipments' || pageHeader ? ` me-dw-client-table me-dw-client-table--${kind}` : ''}`
              }
              tabIndex={0}
              role="region"
              aria-label={t('Activity table')}
            >
              {kind === 'orders' ? (
                <OrderListTable
                  orders={rows}
                  admin={false}
                  showSaleAmount={!adminOverview}
                  showDistributor={adminOverview}
                  distributors={distributors}
                  showHold
                  t={t}
                  locale={locale}
                  onView={(row) => view(row._id)}
                  formatDate={adminOverview ? undefined : formatActivityDate}
                  dateLabel={adminOverview ? 'DATE' : 'Date (UTC)'}
                  statusForOrder={status}
                />
              ) : kind === 'ivrs' ? (
                <IVRListTable
                  records={rows}
                  showDistributor={adminOverview}
                  distributors={distributors}
                  t={t}
                  locale={locale}
                  onView={(row) => view(row._id)}
                  formatDate={adminOverview ? undefined : formatActivityDate}
                  dateLabel={adminOverview ? 'DATE SUBMITTED' : 'Date (UTC)'}
                  label="IVRs"
                  renderStatus={(row) => (
                    <StatusBadge status={status({ ...row, status: ivrOutcome(row) })} />
                  )}
                />
              ) : adminOverview || pageHeader ? (
                <ShipmentListTable
                  shipments={rows}
                  distributors={distributors}
                  showDistributor={adminOverview}
                  t={t}
                  statusForShipment={status}
                  onView={(row) => view(row._id)}
                />
              ) : (
                <DistributorShipmentTable
                  rows={rows}
                  t={t}
                  locale={locale}
                  status={status}
                  view={view}
                />
              )}
            </div>
          )}
          <div className="me-dw-client-pagination flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-[#e2e8f0] bg-[rgba(226,232,240,0.15)]">
            <span className="text-xs font-medium text-[#64748b]">
              {t('Showing {first}–{last} of {total}', {
                first: rows.length ? (page - 1) * pageSize + 1 : 0,
                last: rows.length ? (page - 1) * pageSize + rows.length : 0,
                total,
              })}
            </span>
            {total > pageSize && (
              <div className="me-dw-pagination flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={loading || page === 1}
                  onClick={() => open(kind, page - 1)}
                  className="px-3 h-8 rounded-lg text-[12px] font-semibold bg-[#f7f8fb] text-[#153d48] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e2e8f0] transition-colors"
                >
                  {t('Previous')}
                </button>
                <span>
                  {t('Page {page} of {pages}', { page, pages: Math.ceil(total / pageSize) })}
                </span>
                <button
                  type="button"
                  disabled={loading || page >= Math.ceil(total / pageSize)}
                  onClick={() => open(kind, page + 1)}
                  className="px-3 h-8 rounded-lg text-[12px] font-semibold bg-[#f7f8fb] text-[#153d48] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e2e8f0] transition-colors"
                >
                  {t('Next')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {detail && detailAccess.current === version && kind === 'shipments' && (
        <DistributorShipmentDialog
          key={`${version}:${detail._id}`}
          shipment={detail}
          status={status(detail)}
          adminDistributorId={
            adminDistributorId || (adminOverview ? detailScope.current : undefined)
          }
          onClose={() => setDetail(null)}
          t={t}
        />
      )}
      {detail && detailAccess.current === version && kind !== 'shipments' && (
        <div className="me-dw-subpanel p-4 text-sm" role="region" aria-label={t('Activity detail')}>
          {kind === 'orders' && (
            <OrderDetailsModal
              key={`${version}:${detail._id}`}
              order={detail}
              onClose={() => setDetail(null)}
              canAdminister={false}
              adminDistributorId={
                adminDistributorId || (adminOverview ? detailScope.current : undefined)
              }
              onAction={() => open(kind, page)}
            />
          )}
          {kind === 'ivrs' && (
            <IVRDetailsModal
              key={`${version}:${detail._id}`}
              record={detail}
              onClose={() => setDetail(null)}
              canAdminister={false}
              adminDistributorId={
                adminDistributorId || (adminOverview ? detailScope.current : undefined)
              }
            />
          )}
        </div>
      )}
    </section>
  );
}
